<template>
  <CModal
    title="Domain Registration"
    color="white"
    :show.sync="active"
    :closeOnBackdrop="false"
  >
    <div v-if="loading" class="text-center py-4">
      <CSpinner color="primary" />
      <p class="mt-2 text-muted">Preparing registration form...</p>
    </div>

    <div v-else>
      <div class="vertical-center justify-content-between mb-3">
        <h5>
          {{ restaurant?.restaurant_name }}
          <span v-if="restaurant?.restaurant_city" class="text-muted">
            - {{ restaurant.restaurant_city }}
          </span>
        </h5>
      </div>

      <div class="operation-card selected">
        <div class="mb-2"><strong>Choose a domain</strong></div>
        <div class="d-flex flex-column flex-md-row align-items-stretch align-items-md-center flex-gap-2">
          <CInput
            class="mb-0 flex-fill"
            type="text"
            autocomplete="off"
            :placeholder="domainPlaceholder"
            v-model.trim="domainInput"
            @input="resetCheckState"
          />

          <div class="d-flex flex-column flex-md-row align-items-stretch align-items-md-center flex-gap-2">
            <CButton
              color="info"
              shape="pill"
              :disabled="!canCheck || checking"
              @click="checkAvailability"
            >
              <CSpinner v-if="checking" size="sm" class="mr-1 my-0" />
              <CIcon v-else name="cil-search" class="mr-1 my-0" />
              Check Availability
            </CButton>
          </div>
        </div>

        <transition name="fade">
          <div v-if="checked" class="mt-3">
            <CAlert :color="available ? 'success' : 'danger'" closeButton class="mb-0">
              <div class="d-flex align-items-center">
                <CIcon :name="available ? 'cil-check-circle' : 'cil-x-circle'" size="lg" class="mr-2" />
                <span>{{ checkAvailabilityMessage }}</span>
              </div>
            </CAlert>
          </div>
        </transition>
      </div>
    </div>

    <template #footer>
      <CButton color="link" @click="active = false">Cancel</CButton>
      <CButton 
        color="primary" 
        shape="pill"
        :disabled="!available || submitted"
        @click="registerDomain"
      >
        <CSpinner v-if="submitted" size="sm" /> Register
      </CButton>
    </template>
  </CModal>
</template>

<script>
export default {
  name: 'DomainRegistrationModal',
  props: {
    restaurant: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      checking: false,
      submitted: false,
      checked: false,
      available: false,
      domainInput: '',
      domainPlaceholder: '',
      checkAvailabilityMessage: '',
    };
  },
  computed: {
    active: {
      get() { return this.$store.state.domainRegistrationModalShow; },
      set(value) { this.$store.state.domainRegistrationModalShow = value; }
    },
    canCheck() {
      return this.domainInput && this.domainInput.length > 3 && this.$globalFunc.isDomain(this.domainInput);
    },
  },
  watch: {
    active(newVal) {
      if (newVal) {
        this.prepare();
      }
    }
  },
  methods: {
    prepare() {
      this.loading = true;
      this.checked = false;
      this.available = false;
      
      this.checkAvailabilityMessage = '';

      // Basit bir öneri oluştur
      const base = (this.restaurant?.restaurant_name || '').toString()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '')
        .replace(/^-+|-+$/g, '');

      this.domainPlaceholder = base ? `e.g. ${base}.be` : 'e.g. my-restaurant.be';
      this.domainInput = base ? `${base}.be` : '';
      setTimeout(() => { this.loading = false; }, 150);
    },

    resetCheckState() {
      this.checked = false;
      this.available = false;
    },

    async checkAvailability() {
      if (!this.canCheck) return;
      this.checking = true;

      try {
        const { data } = await this.$axios.post(
          this.$backend.RESTAURANT.DNS_MANAGEMENT.CHECK_DOMAIN_AVAILABILITY,
          { domain: this.domainInput }
        );
        this.available = !!data?.available;
        this.checked = true;

        if (this.available) {
          this.checkAvailabilityMessage = data?.message || 'Domain is available.';
          // Fiyatı bul
          let totalPrice = null;
          if (Array.isArray(data?.offers) && data.offers.length > 0) {
            const offer = data.offers[0];
            if (Array.isArray(offer.prices)) {
              const total = offer.prices.find(p => p.label === "TOTAL");
              if (total && total.price && total.price.text) {
                totalPrice = total.price.text;
              }
            }
          }
          if (totalPrice) {
            this.checkAvailabilityMessage += ` (Total: ${totalPrice})`;
          }
        } else {
          this.checkAvailabilityMessage = data?.message || 'Domain is not available.';
        }
        
        this.$toast[this.available ? 'success' : 'error'](this.checkAvailabilityMessage);
      } catch (e) {
        this.available = false;
        this.checked = true;
        this.$toast.error('Failed to check availability.');
      } finally {
        this.checking = false;
      }
    },

    async registerDomain() {
      if (!this.available || !this.canCheck) return;
      this.submitted = true;
      try {
        const payload = { 
          restaurant_id: this.restaurant?.id || null, 
          domain: this.domainInput 
        };
        const { data } = await this.$axios.post(
          this.$backend.RESTAURANT.DNS_MANAGEMENT.REGISTER_DOMAIN,
          payload
        );
        if (data?.success) {
          this.$toast.success(data?.message || 'Domain registration initiated.');
          this.$emit('registered');
          this.active = false;
        } else {
          this.$toast.error(data?.message || 'Failed to register domain.');
        }
      } catch (e) {
        this.$toast.error(e.response?.data?.message || 'Failed to register domain.');
      } finally {
        this.submitted = false;
      }
    },
  },
};
</script>

<style scoped>
.operation-card {
  background-color: #f8f9fa;
  border: 2px solid #e1e1e1;
  border-radius: 8px;
  padding: 1rem;
}
.operation-card.selected {
  border-color: #007bff;
  background-color: rgba(0, 123, 255, 0.05);
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}
.fade-enter-active, .fade-leave-active { transition: opacity .2s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }
</style>

